import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { cleanTournamentName } from '../utils/cleanTournamentName';

interface TournamentItem {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
}

interface LeagueRound {
  id: number;
  name: string;
  startDate: string | null;
  endDate: string | null;
  status: string;
}

interface CalendarEntry {
  type: 'tournament' | 'round';
  id: number;
  leagueId?: number;
  label: string;
  startDate: Date;
  endDate: Date;
}

const MONTHS_PL = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];

const WEEKDAYS_PL = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'Sb', 'Nd'];

function isoWeekday(date: Date): number {
  return (date.getDay() + 6) % 7;
}

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function toDateOnly(dateStr: string): Date {
  const d = new Date(dateStr);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

const EventCalendar: React.FC = () => {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [entries, setEntries] = useState<CalendarEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tourRes, leagueRes] = await Promise.all([
          fetch('/api/public/events'),
          fetch('/api/public/leagues'),
        ]);

        if (!tourRes.ok || !leagueRes.ok) {
          throw new Error('fetch failed');
        }

        const tourData = await tourRes.json() as { items: TournamentItem[] };
        const leagueData = await leagueRes.json() as { items: { id: number; name: string; status: string }[] };

        const calEntries: CalendarEntry[] = [];

        for (const t of tourData.items) {
          calEntries.push({
            type: 'tournament',
            id: t.id,
            label: cleanTournamentName(t.name),
            startDate: toDateOnly(t.startDate),
            endDate: toDateOnly(t.endDate),
          });
        }

        const activeLeagues = leagueData.items.filter((l) => l.status === 'ACTIVE');

        await Promise.all(
          activeLeagues.map(async (league) => {
            try {
              const roundRes = await fetch(`/api/public/leagues/${league.id}/rounds`);
              if (!roundRes.ok) {
                return;
              }

              const roundData = await roundRes.json() as { rounds: LeagueRound[] };

              for (const round of roundData.rounds) {
                if (!round.startDate) {
                  continue;
                }

                calEntries.push({
                  type: 'round',
                  id: round.id,
                  leagueId: league.id,
                  label: `${cleanTournamentName(league.name)} — ${round.name}`,
                  startDate: toDateOnly(round.startDate),
                  endDate: round.endDate ? toDateOnly(round.endDate) : toDateOnly(round.startDate),
                });
              }
            } catch {
              // Ignore per-league errors
            }
          })
        );

        setEntries(calEntries);
        setLoading(false);
      } catch {
        setError('Nie udało się załadować kalendarza.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  function prevMonth() {
    if (month === 0) {
      setYear((y) => y - 1);
      setMonth(11);
    } else {
      setMonth((m) => m - 1);
    }
  }

  function nextMonth() {
    if (month === 11) {
      setYear((y) => y + 1);
      setMonth(0);
    } else {
      setMonth((m) => m + 1);
    }
  }

  function entriesForDay(day: number): CalendarEntry[] {
    const d = new Date(year, month, day);
    return entries.filter((e) => d >= e.startDate && d <= e.endDate);
  }

  const totalDays = daysInMonth(year, month);
  const firstOffset = isoWeekday(new Date(year, month, 1));

  const cells: (number | null)[] = [];

  for (let i = 0; i < firstOffset; i++) {
    cells.push(null);
  }

  for (let d = 1; d <= totalDays; d++) {
    cells.push(d);
  }

  while (cells.length % 7 !== 0) {
    cells.push(null);
  }

  const isToday = (day: number) =>
    day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  if (loading) {
    return <div className="events-loading">Ładowanie...</div>;
  }

  if (error) {
    return <div className="events-error">{error}</div>;
  }

  return (
    <div className="calendar">
      <Link to="/events" className="event-detail-back">
        ← Powrót do wydarzeń
      </Link>

      <div className="calendar-header">
        <button onClick={prevMonth} className="calendar-nav" aria-label="Poprzedni miesiąc">
          ‹
        </button>
        <h1 className="calendar-title">
          <span className="calendar-month">{MONTHS_PL[month]}</span>
          <span className="calendar-year">{year}</span>
        </h1>
        <button onClick={nextMonth} className="calendar-nav" aria-label="Następny miesiąc">
          ›
        </button>
      </div>

      <div className="calendar-grid">
        {WEEKDAYS_PL.map((wd) => (
          <div key={wd} className="calendar-weekday">{wd}</div>
        ))}
        {cells.map((day, index) => (
          <div
            key={index}
            className={`calendar-cell${day ? '' : ' calendar-cell-empty'}${day && isToday(day) ? ' calendar-cell-today' : ''}`}
          >
            {day && (
              <>
                <div className="calendar-day-num">{day}</div>
                {entriesForDay(day).map((entry) => (
                  <Link
                    key={`${entry.type}-${entry.id}`}
                    to={entry.type === 'tournament' ? `/events/${entry.id}` : `/leagues/${entry.leagueId}`}
                    className={`calendar-entry calendar-entry-${entry.type}`}
                    title={entry.label}
                  >
                    {entry.label}
                  </Link>
                ))}
              </>
            )}
          </div>
        ))}
      </div>

      <div className="calendar-legend">
        <span className="calendar-legend-item">
          <span className="calendar-legend-swatch calendar-entry-tournament" />
          Turniej
        </span>
        <span className="calendar-legend-item">
          <span className="calendar-legend-swatch calendar-entry-round" />
          Kolejka ligi
        </span>
      </div>
    </div>
  );
};

export default EventCalendar;
